import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { SHOWCASE_BANNERS } from '../../data/content';
import { scrollToSection } from '../../lib/scroll';

const Showcase = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(
      () => setIndex((i) => (i + 1) % SHOWCASE_BANNERS.length),
      6000
    );
    return () => clearInterval(timer);
  }, []);

  const banner = SHOWCASE_BANNERS[index];
  const prev = () => setIndex((i) => (i - 1 + SHOWCASE_BANNERS.length) % SHOWCASE_BANNERS.length);
  const next = () => setIndex((i) => (i + 1) % SHOWCASE_BANNERS.length);

  return (
    <section data-testid="showcase-section" className="relative py-24 bg-[#040D1A]">
      <div className="container mx-auto px-4">
        <div className="relative rounded-3xl overflow-hidden border border-amber-400/15 h-[480px] md:h-[560px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={banner.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7 }}
              className="absolute inset-0"
            >
              <motion.img
                src={banner.image}
                alt={banner.title}
                initial={{ scale: 1.08 }}
                animate={{ scale: 1 }}
                transition={{ duration: 6, ease: 'linear' }}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#040D1A] via-[#040D1A]/70 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#040D1A]/80 via-transparent to-transparent" />
            </motion.div>
          </AnimatePresence>

          <div className="absolute inset-0 flex items-center">
            <div className="px-8 md:px-16 max-w-2xl">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`content-${banner.id}`}
                  initial={{ opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                >
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-300 mb-4">
                    {banner.kicker}
                  </p>
                  <h2 className="font-display font-extrabold uppercase text-3xl md:text-5xl text-white leading-tight tracking-tight">
                    {banner.title}
                  </h2>
                  <p className="mt-3 text-lg md:text-xl text-amber-300 font-semibold">
                    {banner.subtitle}
                  </p>
                  <p className="mt-2 text-slate-300">{banner.description}</p>
                  <button
                    data-testid={`showcase-cta-${banner.id}`}
                    onClick={() => scrollToSection(banner.target)}
                    className="group mt-7 inline-flex items-center gap-2 bg-amber-400 text-[#0A2847] font-display font-bold uppercase tracking-wide text-sm px-7 py-3.5 rounded-full hover:bg-amber-300 hover:shadow-[0_0_28px_rgba(252,211,77,0.4)] transition-all duration-300"
                  >
                    {banner.buttonText}
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <div className="flex gap-2">
            {SHOWCASE_BANNERS.map((b, i) => (
              <button
                key={b.id}
                data-testid={`showcase-dot-${i}`}
                onClick={() => setIndex(i)}
                aria-label={`Ir para banner ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  index === i ? 'w-10 bg-amber-400' : 'w-4 bg-slate-600 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>
          <div className="flex gap-3">
            <button
              data-testid="showcase-prev"
              onClick={prev}
              aria-label="Banner anterior"
              className="w-11 h-11 rounded-full border border-amber-400/25 text-amber-300 flex items-center justify-center hover:bg-amber-400/10 hover:border-amber-300 transition-all"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              data-testid="showcase-next"
              onClick={next}
              aria-label="Próximo banner"
              className="w-11 h-11 rounded-full border border-amber-400/25 text-amber-300 flex items-center justify-center hover:bg-amber-400/10 hover:border-amber-300 transition-all"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Showcase;
